import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import api from '../../services/api';

/**
 * Monthly Revenue Trend chart (Feb 2026, iter276).
 *
 * Backed by `/api/admin/revenue/monthly-trend` which aggregates all
 * revenue sources (enrollments, lane_bookings, tournament_registrations,
 * camp_registrations, manual_invoices, monthly_invoices,
 * small_group_invoices) and subtracts refunds. The totals shown here
 * sum to the headline numbers on the home Revenue card AND the Billing
 * Management overview — they all flow through
 * `services.revenue_aggregator.compute_revenue_for_window`.
 */
const RANGE_OPTIONS = [
  { label: '6m', months: 6 },
  { label: '12m', months: 12 },
  { label: '24m', months: 24 },
];

const CATEGORY_COLORS = {
  subscriptions: '#6366f1',
  coaching:      '#10b981',
  tournaments:   '#f59e0b',
  lane_bookings: '#14b8a6',
  camps:         '#ec4899',
  other:         '#64748b',
};

const CATEGORY_LABELS = {
  subscriptions: 'Subscriptions',
  coaching:      'Coaching',
  tournaments:   'Tournaments',
  lane_bookings: 'Lane Bookings',
  camps:         'Camps',
  other:         'Other',
};

const fmt = (n) => `${cs()}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const MonthlyRevenueTrendChart = () => {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (m) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/revenue/monthly-trend?months=${m}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load trend');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(months);
  }, [months]);

  // Flatten by-category breakdown so each is a top-level field that
  // recharts can read directly.
  const chartData = (data?.series || []).map((row) => ({
    ...row,
    ...row.by_category,
  }));

  // Visible categories — only those that have non-zero data anywhere in
  // the series so we don't clutter the chart with always-empty bars.
  const visibleCategories = Object.keys(CATEGORY_COLORS).filter((cat) =>
    chartData.some((row) => (row[cat] || 0) > 0)
  );

  return (
    <Card data-testid="monthly-revenue-trend-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Cash Flow Trend
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Net revenue per month across all streams. Matches dashboard totals exactly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md bg-slate-100 p-1" role="tablist">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.months}
                  type="button"
                  onClick={() => setMonths(opt.months)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    months === opt.months
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`trend-range-${opt.label}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load(months)}
              disabled={loading}
              data-testid="trend-refresh-btn"
              aria-label="Refresh chart"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-sm text-rose-600 p-4 bg-rose-50 rounded">{error}</div>
        ) : loading && !data ? (
          <div className="h-72 flex items-center justify-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : !chartData.length ? (
          <div className="h-72 flex items-center justify-center text-slate-400 text-sm">
            No revenue recorded yet.
          </div>
        ) : (
          <>
            {/* Summary strip — totals over the chosen window. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
              <div className="bg-emerald-50 border border-emerald-100 rounded-md p-2">
                <p className="text-emerald-700 uppercase tracking-wide">Gross</p>
                <p className="text-base font-semibold text-emerald-900" data-testid="trend-totals-gross">
                  {fmt(data?.totals?.gross)}
                </p>
              </div>
              <div className="bg-rose-50 border border-rose-100 rounded-md p-2">
                <p className="text-rose-700 uppercase tracking-wide">Refunded</p>
                <p className="text-base font-semibold text-rose-900" data-testid="trend-totals-refunded">
                  −{fmt(data?.totals?.refunded)}
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                <p className="text-slate-600 uppercase tracking-wide">Net</p>
                <p className="text-base font-semibold text-slate-900" data-testid="trend-totals-net">
                  {fmt(data?.totals?.net)}
                </p>
              </div>
              <div className="bg-indigo-50 border border-indigo-100 rounded-md p-2">
                <p className="text-indigo-700 uppercase tracking-wide">Transactions</p>
                <p className="text-base font-semibold text-indigo-900" data-testid="trend-totals-txns">
                  {data?.totals?.transactions || 0}
                </p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="netRevenueArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v)} width={70} />
                <Tooltip
                  formatter={(value, name) => [fmt(value), CATEGORY_LABELS[name] || name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => CATEGORY_LABELS[v] || v} />
                {visibleCategories.map((cat) => (
                  <Bar
                    key={cat}
                    dataKey={cat}
                    stackId="cats"
                    fill={CATEGORY_COLORS[cat]}
                    name={cat}
                  />
                ))}
                <Area
                  type="monotone"
                  dataKey="net"
                  name="net"
                  stroke="#059669"
                  strokeWidth={2}
                  fill="url(#netRevenueArea)"
                  dot={{ r: 3, fill: '#059669' }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="refunded"
                  name="refunded"
                  stroke="#e11d48"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <p className="text-[11px] text-slate-400 mt-2 text-center">
              Bars are category breakdown · Green area is net revenue · Dashed red line is refunds
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MonthlyRevenueTrendChart;
